import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LayersIcon } from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { EmptyState } from '@/components/empty-state';
import { MediaLibrary } from '@/components/media/media-library';
import { PageHeader } from '@/components/page-header';
import { PermissionGate } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { messageForError } from '@/lib/api-client';
import { sitesQuery } from '@/lib/api/sites';

/** Assets are stored per site, so the library is opened one site at a time. */
function MediaPage() {
  const { orgId } = Route.useParams();
  const sites = useQuery(sitesQuery(orgId));
  const [siteId, setSiteId] = useState<string | null>(null);

  useEffect(() => {
    if (siteId === null && sites.data?.[0]) setSiteId(sites.data[0].id);
  }, [siteId, sites.data]);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="Workspace"
        title="Media"
        description="Images and documents that image and file fields point at. Each asset belongs to one site, so keys and storage stay isolated between them."
        actions={
          (sites.data?.length ?? 0) > 1 ? (
            <div className="flex items-center gap-2">
              <Label htmlFor="media-site" className="section-label">
                Site
              </Label>
              <Select value={siteId ?? ''} onValueChange={setSiteId}>
                <SelectTrigger id="media-site" className="h-8 w-56">
                  <SelectValue placeholder="Choose a site" />
                </SelectTrigger>
                <SelectContent>
                  {(sites.data ?? []).map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : undefined
        }
      />

      {sites.isLoading ? <Skeleton className="h-64 w-full rounded-lg" /> : null}

      {sites.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-data text-danger"
        >
          {messageForError(sites.error, 'The site list could not be loaded.')}
        </div>
      ) : null}

      {!sites.isLoading && (sites.data?.length ?? 0) === 0 && !sites.isError ? (
        <EmptyState
          icon={LayersIcon}
          title="No sites yet"
          description="Media is stored per site. Connect a site and its library opens here."
          action={
            <PermissionGate permission={PERMISSIONS.SITE_VIEW}>
              <Button asChild>
                <Link to="/app/$orgId/sites" params={{ orgId }}>
                  Go to sites
                </Link>
              </Button>
            </PermissionGate>
          }
        />
      ) : null}

      {siteId ? <MediaLibrary key={siteId} orgId={orgId} siteId={siteId} /> : null}
    </div>
  );
}

export const Route = createFileRoute('/app/$orgId/media')({
  component: MediaPage,
});
